//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

type UploadStoreListener = () => void;

interface UploadStoreState {
  pendingBrandBriefFilename: string | undefined;
}

type CampaignRequestId = string | number;

const EMPTY_UPLOAD_STATE: UploadStoreState = {
  pendingBrandBriefFilename: undefined,
};

const createUploadStore = () => {
  const states = new Map<string, UploadStoreState>();
  const keysToClearWhenIdle = new Set<string>();
  const listeners = new Set<UploadStoreListener>();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const getState = (campaignRequestId: CampaignRequestId | undefined) => {
    if (campaignRequestId === undefined) {
      return EMPTY_UPLOAD_STATE;
    }

    return states.get(String(campaignRequestId)) ?? EMPTY_UPLOAD_STATE;
  };

  const setState = (
    campaignRequestId: CampaignRequestId,
    partial: Partial<UploadStoreState>,
  ) => {
    const key = String(campaignRequestId);
    const nextState = { ...getState(campaignRequestId), ...partial };
    if (
      nextState.pendingBrandBriefFilename === undefined &&
      keysToClearWhenIdle.has(key)
    ) {
      states.delete(key);
      keysToClearWhenIdle.delete(key);
    } else {
      states.set(key, nextState);
    }
    notify();
  };

  const clearState = (campaignRequestId: CampaignRequestId) => {
    const key = String(campaignRequestId);
    states.delete(key);
    keysToClearWhenIdle.delete(key);
    notify();
  };

  const clearStateWhenIdle = (campaignRequestId: CampaignRequestId) => {
    const key = String(campaignRequestId);
    if (getState(campaignRequestId).pendingBrandBriefFilename === undefined) {
      clearState(campaignRequestId);
      return;
    }

    keysToClearWhenIdle.add(key);
  };

  const subscribe = (listener: UploadStoreListener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return {
    clearState,
    clearStateWhenIdle,
    getState,
    setState,
    subscribe,
  };
};

export const brandBriefUploadStore = createUploadStore();
